import i18n from '@/js/i18n';
import api from '@/services/api';
import store, { encryptStorage } from '@/store';

import { getQueryParamsValue } from '@/helpers/AppHelpers';
import { PageMapping } from '@/helpers/PageMappingHelpers';
import { playerPaymentProfileStatus } from '@/helpers/ProfileHelpers';
import { erreurDefaut } from '@/helpers/RouteHelpers';
import { capitalize } from '@/helpers/StringHelpers';
import { formatteCodePostal } from '@/helpers/AddressHelpers';
import { processSalesforceClientMigrated } from '@/services/api/compte/profile';

import session from '@/services/api/compte/session';

export default {
  async identityValidation() {
    const compteState = store.state.compte;
    const data = {
      profile: {
        address: {
          addressLine1: compteState.adresse.numero,
          addressLine2: compteState.adresse.rue,
          addressLine3: compteState.adresse.appartement,
          city: compteState.adresse.ville,
          postcode: compteState.adresse.codePostal,
          countryCode: 'CAN',
          state: 'QC',
        },
      },
      custUUID: sessionStorage.getItem('lq_client_uuid'),
      dossier: {
        clientMigrated: store.state.dossier.clientMigrated,
        versionDonnees: store.state.dossier.versionDonnees,
      },
    };
    return new Promise((resolve, reject) => {
      api
        .call('players/identity/validation', 'identityValidation', 'POST', {
          headers: {
            'cache-control': 'no-cache',
            'Content-Type': 'application/json',
            pragma: 'no-cache',
          },
          data,
        })
        .then((response) => {
          if (response.data?.playerProfile) {
            // Nouvelle version données après validation de l'identité
            const versionDonnees = response.data.playerProfile?.versionDonnees;
            store.commit('dossier/SET_DOSSIER_VERSION_DONNEES', versionDonnees);
          }
          resolve(response);
        })
        .catch((error) => {
          console.error('identityValidation | failed: ', error);
          reject(error);
        });
    });
  },

  async verifierIdentite() {
    if (store.state.dossier.clientMigrated === null) {
      await processSalesforceClientMigrated();
    }
    return _creerVerification()
      .then(async (data) => {
        if (data && data.status === 'INITIATED' && data.id && data.id !== 'null') {
          console.log('creerVerification | Response : ', JSON.stringify(data));
          const details = {
            links: data.links,
            status: data.status,
            verificationId: data.id,
          };
          // Sauvegarder clientMigrated et versionDonnees avant d'aller à VerifiedMe
          encryptStorage.setItem('clientMigrated', store.state.dossier.clientMigrated);
          encryptStorage.setItem('versionDonnees', store.state.dossier.versionDonnees);

          // Sauvegarde du verificationId dans un cookie pour le fetchVerification - VerifiedMe
          // eslint-disable-next-line no-undef
          ejApi.ecrireCookie('verifId', data.id, 0.014);

          throw { url: data.links[0].href };
        } else {
          throw {
            url: PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl,
            message: data.code,
          };
        }
      })
      .catch((error) => {
        console.error('creerVerification | fail: ', error);
        throw {
          url: error.url ?? PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl,
          message: error.message,
        };
      });
  },
  async recupererVerificationInfo() {
    await _recupererVerificationInfo()
      .then(async (data) => {
        const verification = data ?? {};
        let adresse = {};
        let nominativeData = {};

        store.commit('profile/SET_VERIFICATION', verification);

        if (verification.status == 'SUCCESS') {
          if (verification.nominativeData) {
            adresse = formatteAdresseAffichage(verification.nominativeData.address);
            // Sauvegarde de l'adresse pour l'affichage dans le store
            store.commit('profile/SET_VERIF_ADRESSE_AFFICHAGE', adresse);

            nominativeData = formatteNominativeData(verification.nominativeData, adresse);
            // Sauvegarde des nominativeData dans le store
            store.commit('profile/SET_VERIF_USER_INFO', nominativeData);
          }
        } else {
          throw { url: PageMapping.IDENTITE_SERVICE_ECHEC.siteUrl };
        }
      })
      .catch((error) => {
        console.error('recupererVerificationInfo | fail: ', error);
        throw {
          url: error.url ?? PageMapping.ERREUR_GENERIQUE.siteUrl,
          message: error.message,
        };
      });
  },

  async sauvegarderVerificationInfoAccepte(recaptchaToken) {
    await _sauvegarderVerificationInfo(recaptchaToken, 'APPROVED')
      .then(() => {
        store.commit('profile/RESET_VERIF_DETAILS');
        throw { url: PageMapping.IDENTITE_CONFIRMATION.siteUrl };
      })
      .catch((error) => {
        throw {
          url: error.url ?? PageMapping.ERREUR_GENERIQUE.siteUrl,
          message: error.message,
        };
      });
  },
  async sauvegarderVerificationInfoRefuse(recaptchaToken) {
    await _sauvegarderVerificationInfo(recaptchaToken, 'DECLINED')
      .then(() => {
        store.commit('profile/RESET_VERIF_DETAILS');
        throw { url: PageMapping.IDENTITE_DOCUMENTS.siteUrl };
      })
      .catch((error) => {
        throw {
          url: error.url ?? PageMapping.ERREUR_GENERIQUE.siteUrl,
          message: error.message,
        };
      });
  },
};

async function _creerVerification() {
  // Construction URL de retour VerifiedMe: Page '/compte/identite-service-validation'
  const pathname =
    i18n.locale == 'en'
      ? PageMapping.IDENTITE_SERVICE_VALIDATION.siteUrlEn
      : PageMapping.IDENTITE_SERVICE_VALIDATION.siteUrl;

  const data = {
    lang: i18n.locale,
    returnUrl: `${window.location.protocol}//${window.location.hostname}/${i18n.locale}${pathname}`,
  };

  return new Promise((resolve, reject) => {
    const headers = {
      'Content-Type': 'application/json',
      'request-id': 'create-verification',
    };
    api
      .call('account-api/v2/players/identity/verification', 'createIdentityVerification', 'POST', {
        withCredentials: true,
        headers,
        data,
      })
      .then((response) => {
        if (response.data.errors) {
          reject({ url: PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl });
        } else {
          store.commit('profile/RESET_VERIF_DETAILS');
          resolve(response.data);
        }
      })
      .catch((error) => {
        console.error('createIdentityVerification | fail:  ', error);
        reject(error);
      });
  });
}

async function _recupererVerificationInfo() {
  // eslint-disable-next-line no-undef
  const verificationId = ejApi.lireCookie('verifId');
  const status = getQueryParamsValue('status');
  const statusMessage = getQueryParamsValue('statusMessage')?.toLowerCase();
  const profileStatusHasUploadID = store.getters['profile/hasStatus'](
    playerPaymentProfileStatus.UPLOAD_ID
  );

  // Redirection si status === 'FAILED' && (statusMessage === 'User cancelled' || statusMessage === 'Session expired')
  if (status === 'FAILED') {
    if (statusMessage === 'user cancelled') {
      if (profileStatusHasUploadID) {
        throw {
          url: PageMapping.IDENTITE_DOCUMENTS_ENVOYES_SERVICE.siteUrl,
          message: statusMessage,
        };
      }
      throw {
        url: PageMapping.IDENTITE_OPTIONS_VERIFICATION.siteUrl,
        message: statusMessage,
      };
    }
    if (statusMessage === 'session expired') {
      throw {
        url: PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl,
        message: statusMessage,
      };
    }
  }
  // Redirection si session Paysafe expirée ("Time out après 30 minutes")
  if (status === 'EXPIRED') {
    throw {
      url: PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl,
      message: statusMessage,
    };
  }
  if (store.state.dossier.clientMigrated === null) {
    try {
      await processSalesforceClientMigrated();
    } catch (error) {
      throw {
        url: PageMapping.ERREUR_GENERIQUE.siteUrl,
      };
    }
  }
  return new Promise((resolve, reject) => {
    const headers = {
      'Content-Type': 'application/json',
      'request-id': 'fetch-identity-verification',
    };
    const data = {
      idClient: sessionStorage.getItem('lq_client_uuid'),
      versionDonnees: store.state.dossier.versionDonnees,
      progressiveMigration: process.env.VUE_APP_PROG_MIGRATION === 'true',
      clientMigrated: store.state.dossier.clientMigrated,
    };
    console.log(data);
    api
      .call(
        `account-api/v2/players/identity/verification/${verificationId}`,
        'getIdentityVerificationInfo',
        'POST',
        {
          withCredentials: true,
          headers,
          data,
        }
      )
      .then((response) => {
        if (response.data.errors) {
          reject({ url: PageMapping.ERREUR_GENERIQUE.siteUrl });
        } else {
          store.commit('profile/RESET_VERIF_INFO');
          if (response.data?.versionDonnees) {
            // Nouvelle version données après récupération de l'identité
            const versionDonnees = response.data?.versionDonnees;
            store.commit('dossier/SET_DOSSIER_VERSION_DONNEES', versionDonnees);
          }
          resolve(response.data);
        }
      })
      .catch((error) => {
        console.error('recupererVerificationInfo | failed: ', error);
        reject(error);
      });
  });
}

async function _sauvegarderVerificationInfo(recaptchaToken, customerDecision) {
  // Restaurer clientMigrated, versionDonnees sauvegardé avant le redirect VerifiedMe
  store.commit('dossier/SET_DOSSIER_CLIENT_MIGRATED', encryptStorage.getItem('clientMigrated'));
  store.commit('dossier/SET_DOSSIER_VERSION_DONNEES', encryptStorage.getItem('versionDonnees'));

  // eslint-disable-next-line no-undef
  const verificationId = ejApi.lireCookie('verifId');
  const verifInfo = await store.dispatch('profile/getVerificationInfo');
  const profileStatusHasUploadID = store.getters['profile/hasStatus'](
    playerPaymentProfileStatus.UPLOAD_ID
  );
  const data = {
    id: verificationId,
    customerDecision,
    address: verifInfo.address,
    dossier: {
      idClient: sessionStorage.getItem('lq_client_uuid'),
      versionDonnees: store.state.dossier.versionDonnees,
      clientMigrated: store.state.dossier.clientMigrated,
      progressiveMigration: process.env.VUE_APP_PROG_MIGRATION === 'true',
    },
  };
  const headers = {
    'Content-Type': 'application/json',
    'request-id': 'save-verification-info',
    'g-recaptcha': recaptchaToken,
  };
  return new Promise((resolve, reject) => {
    api
      .call('account-api/v2/players/identity/update', 'saveIdentityVerificationInfo', 'POST', {
        withCredentials: true,
        headers,
        data,
      })
      .then((response) => {
        console.log('sauvegarderVerificationInfo | response: ', response.data);
        if (response.data?.versionDonnees) {
          // Nouvelle version données après sauvegarde de l'identité accepté ou refusé
          const versionDonnees = response.data?.versionDonnees;
          store.commit('dossier/SET_DOSSIER_VERSION_DONNEES', versionDonnees);
        }
        if (response.data && response.data.verificationType === 'VerifiedMe') {
          switch (response.data.validationStatus) {
            case 'OB_OK':
              // Happy Path
              // CCC-17698 Reset store data only if user is fully subscribed.
              store.commit('compte/RESET');
              store.commit('profile/RESET');
              resolve(response.data);
              break;
            case 'IDENTITYMANUAL':
              reject({ url: PageMapping.IDENTITE_ECHEC.siteUrl });
              break;
            case 'FAILEDVERIFIEDME':
            case 'VERIFIEDMEPENDINGID':
              if (profileStatusHasUploadID) {
                reject(erreurDefaut);
              }
              reject({ url: PageMapping.IDENTITE_DOCUMENTS.siteUrl });
              break;
            case 'IDENTITYFAIL':
            case 'PENDINGID':
            case 'WATCHLIST':
              console.error(
                'sauvegarderVerificationInfo | failed: ',
                response.data.validationStatus
              );
              reject({ url: PageMapping.IDENTITE_ECHEC.siteUrl });
              break;
            case 'URNFAILURE':
              session.fermerSession().then(() => {
                reject({ url: PageMapping.IDENTITE_ECHEC_DOUBLON.siteUrl });
              });
              break;
            case 'CARDEXFAILURE':
              session.fermerSession().then(() => {
                reject({
                  url: PageMapping.IDENTITE_ECHEC_AUTOEXCLUSION.siteUrl,
                });
              });
              break;
            case 'ERROREXTERNALSERVICE':
              console.error(
                'sauvegarderVerificationInfo | failed : ',
                response.data.validationStatus
              );
              reject({ url: PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl });
              break;
            case 'PAYSAFE_PROFILE_CREATION_FAILED':
              console.error(
                'sauvegarderVerificationInfo | Paysafe Profile Creation failed : ',
                response.data.messages
              );
              reject({ url: PageMapping.IDENTITE_ECHEC.siteUrl });
              break;
            default:
              reject({ url: PageMapping.ERREUR_GENERIQUE.siteUrl });
              break;
          }
        } else {
          reject({ url: PageMapping.ERREUR_GENERIQUE.siteUrl });
        }
      })
      .catch((error) => {
        console.error('sauvegarderVerificationInfo | failed: ', error);
        reject(error);
      })
      .finally(() => {
        encryptStorage.removeItem('clientMigrated');
        encryptStorage.removeItem('versionDonnees');
      });
  });
}

function formatteNominativeData(nominativeData, adresse) {
  // Formatttage firstname, lastName et Adresse(au format OpenBet)
  return {
    ...nominativeData,
    firstname: capitalize(nominativeData.firstname),
    lastname: capitalize(nominativeData.lastname),
    address: formatAdresseOpenbet(adresse),
  };
}

function formatAdresseOpenbet(adresse) {
  let addressOpenbet = null;
  if (adresse.appartement) {
    // Adresse avec un numéro d'appartement
    addressOpenbet = {
      apartmentNumber: adresse.appartement,
      civicNumber: adresse.numero,
      street: adresse.rue,
      city: adresse.ville,
      postcode: adresse.codePostal,
      state: 'QC',
    };
  } else {
    // Adresse sans numéro d'appartement
    addressOpenbet = {
      civicNumber: adresse.numero,
      street: adresse.rue,
      city: adresse.ville,
      postcode: adresse.codePostal,
      state: 'QC',
    };
  }
  return addressOpenbet;
}

export function formatteAdresseAffichage(address) {
  let splitAddress = null;
  let appartement = null;
  let numeroCivique = null;
  let rue = null;
  let streetAddress = null;

  // Formattage de l'adresse
  // Extraction du numéro d'appartement et du numéro civique de la rue
  splitAddress = address.street.split(' ');
  numeroCivique = splitAddress.shift();
  streetAddress = splitAddress.join(' ');
  if (/App.|Apt./.test(streetAddress)) {
    splitAddress = streetAddress.split(` ${i18n.t('libAppartement')} `);
    streetAddress = splitAddress.shift();
    appartement = splitAddress.join('');
  }
  if (numeroCivique.includes('-')) {
    const splitNumeroCivique = numeroCivique.split('-');
    appartement = splitNumeroCivique.shift();
    numeroCivique = splitNumeroCivique.join('');
  }

  // Tronquer le no.civique retourné par Interac/VerifiedMe
  numeroCivique = numeroCivique.substring(0, 10);

  rue = capitalize(streetAddress);
  const ville = capitalize(address.city);
  // formattage du code postal pour l'affichage
  let codePostal = formatteCodePostal(address.postcode);

  // Formattage des lignes d'adresse pour l'affichage
  const numeroCiviqueRue = [numeroCivique, rue].join(', ');
  const ligneAdresse = appartement
    ? `${numeroCiviqueRue} ${i18n.t('libAppartement')} ${appartement}`
    : numeroCiviqueRue;
  const ligneAdresse2 = `${ville} (QC) ${codePostal}`;
  // Enlève les espaces dans le code postal
  const pattern = /\\s/g;
  codePostal = codePostal.replace(new RegExp(pattern), '');
  // Adresse formattée au format d'affichage page '/compte/identite-service-validation'
  const adresse = {
    numero: numeroCivique,
    rue,
    appartement,
    ville,
    codePostal,
    state: 'QC',
    ligneAdresse,
    ligneAdresse2,
  };

  return adresse;
}
